import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

import { PopupNames } from 'core';
import {
  useInvoiceMatching,
  usePaymentMutations,
  usePopupStateOperations,
} from 'core/hooks';
import { PATHS } from 'router/config';
import { MoneyCell } from 'molecules';

import * as L from '../StudentDetail.styled';
import { AddContributionInlinePopup } from './AddContributionInlinePopup';
import { AddPaymentInlinePopup } from './AddPaymentInlinePopup';
import { PickContributionForPaymentPopup } from './PickContributionForPaymentPopup';
import { PickInvoiceForContributionPopup } from './PickInvoiceForContributionPopup';
import { IAgreement, IContribution, IPayment, IStudent } from './types';

interface IPaymentsBlockProps {
  agreement: IAgreement;
  student: IStudent;
  studentId: string;
  studentEmail: string | null;
  onChanged?: () => void;
  hideAdd?: boolean;
}

type ContributionStatus = 'paid' | 'partial' | 'overdue' | 'upcoming';

const statusOf = (c: IContribution): ContributionStatus => {
  if (c.complete) {
    const rem = c.remainder ?? 0;
    return rem > 0 ? 'partial' : 'paid';
  }
  return (c.delay ?? 0) > 0 ? 'overdue' : 'upcoming';
};

const STATUS_TONE: Record<
  ContributionStatus,
  'ok' | 'warn' | 'danger' | 'neutral'
> = {
  paid: 'ok',
  partial: 'warn',
  overdue: 'danger',
  upcoming: 'neutral',
};

/**
 * Schedule-first view of an agreement's money flow — single invariant:
 * every Payment closes a planned Contribution. No orphan payments ever
 * appear in normal operation.
 *
 *   - Main table: one row per `Contribution`. Status is computed from
 *     `complete` + `remainder` + `delay` (§9.5 semantics). Actions
 *     depend on status:
 *       * unpaid → `+ Оплатить` (creates Payment + closes contribution
 *         atomically via `createPayment({ contributionId })`) and
 *         `Привязать счёт` (creates Payment from a Fakturownia invoice
 *         and atomically attaches both the invoice and the contribution
 *         — see `PickInvoiceForContributionPopup`).
 *       * paid / partial → `Удалить оплату` deletes the Payment outright
 *         (with prior `unlinkPayment` if a Fakturownia invoice is
 *         attached). Backend resets `contribution.payment = null` on
 *         delete, the row returns to `⏳ Ожидается`. Note: we don't
 *         offer `unlinkContribution` anymore because that would create
 *         the very orphan we're trying to eliminate.
 *
 *   - Header carries `+ Взнос в план`: a financier-driven add. Calls
 *     `editStudentWithAgreement` with the existing schedule + a new
 *     row appended.
 *
 *   - «Требуют привязки» migration block at the bottom — visible only if
 *     legacy orphan Payments (`payment.contribution == null`) exist for
 *     this agreement. Each row offers `Привязать к взносу` (opens
 *     `PickContributionForPaymentPopup`) and, for orphan-without-invoice
 *     rows, `Удалить`. Once all rows are resolved the block disappears.
 */
export const PaymentsBlock = memo(
  ({
    agreement,
    student,
    studentId,
    studentEmail,
    onChanged,
    hideAdd,
  }: IPaymentsBlockProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const {
      operations: { showPopup },
    } = usePopupStateOperations();
    const { unlinkPayment, loading: linkLoading } = useInvoiceMatching();
    const { deletePayment, loading: mutLoading } = usePaymentMutations(
      studentId,
    );

    const [addingContribution, setAddingContribution] = useState(false);
    const [
      payingContribution,
      setPayingContribution,
    ] = useState<IContribution | null>(null);
    const [
      linkingContribution,
      setLinkingContribution,
    ] = useState<IContribution | null>(null);
    const [migratingPayment, setMigratingPayment] = useState<IPayment | null>(
      null,
    );

    const contributions: IContribution[] = useMemo(
      () =>
        (agreement.contributions?.edges || [])
          .map((edge) => edge?.node)
          .filter((node): node is IContribution => Boolean(node)),
      [agreement.contributions],
    );

    const payments: IPayment[] = useMemo(
      () => (agreement.payments || []).filter((p): p is IPayment => Boolean(p)),
      [agreement.payments],
    );

    // Legacy data-fix surface: any Payment that doesn't close a
    // Contribution shouldn't exist in the new model. We surface it so the
    // operator can attach it without a separate admin tool.
    const orphanPayments = useMemo(
      () => payments.filter((p) => !p.contribution),
      [payments],
    );

    const onDeletePaidContribution = useCallback(
      (c: IContribution) => {
        const { payment } = c;
        if (!payment) return;
        const dateStr = moment(payment.date).format('DD.MM.YYYY');
        const amount = `${payment.amountAgreement} ${
          payment.currency || agreement.currency || ''
        }`.trim();
        const invoice = payment.fakturowniaInvoice;
        const invoiceLabel =
          invoice?.number || (invoice ? `#${invoice.pk}` : '');
        const title = t(
          'pages.studentDetail.payments.schedule.deletePaymentTitle',
        );
        const body = invoice
          ? t(
              'pages.studentDetail.payments.schedule.deletePaymentWithInvoiceBody',
              { date: dateStr, amount, invoice: invoiceLabel },
            )
          : t('pages.studentDetail.payments.schedule.deletePaymentBody', {
              date: dateStr,
              amount,
            });
        showPopup(PopupNames.zoomDangerConfirm, {
          title,
          body,
          confirmString: dateStr,
          onConfirm: async () => {
            // Backend forbids deletePayment while an invoice is linked.
            // Unlink first, then delete; backend then clears
            // contribution.payment on delete.
            if (invoice) {
              const unlinked = await unlinkPayment(invoice.id);
              if (!unlinked) return;
            }
            const ok = await deletePayment(payment.id);
            if (ok && onChanged) onChanged();
          },
        });
      },
      [
        showPopup,
        unlinkPayment,
        deletePayment,
        onChanged,
        t,
        agreement.currency,
      ],
    );

    const onDeleteOrphanPayment = useCallback(
      (p: IPayment) => {
        if (p.fakturowniaInvoice) return;
        const dateStr = moment(p.date).format('DD.MM.YYYY');
        const amount = `${p.amountAgreement} ${
          p.currency || agreement.currency || ''
        }`.trim();
        showPopup(PopupNames.zoomDangerConfirm, {
          title: t('pages.studentDetail.payments.dangerTitle'),
          body: t('pages.studentDetail.payments.dangerBody', {
            date: dateStr,
            amount,
          }),
          confirmString: dateStr,
          onConfirm: async () => {
            const ok = await deletePayment(p.id);
            if (ok && onChanged) onChanged();
          },
        });
      },
      [showPopup, deletePayment, onChanged, t, agreement.currency],
    );

    const openInvoiceDetail = useCallback(
      (invoiceId: string) =>
        history.push(PATHS.financesInvoice.replace(':id', invoiceId)),
      [history],
    );

    return (
      <L.Subblock>
        {/* ─────────── SCHEDULE ─────────── */}
        <L.SubblockHeader>
          {t('pages.studentDetail.payments.schedule.title')}
          <L.SubblockHeaderCount>
            · {contributions.length}
          </L.SubblockHeaderCount>
          {!hideAdd ? (
            <L.SubblockRight>
              <L.PrimaryActionButton
                type="button"
                onClick={() => setAddingContribution(true)}
                disabled={mutLoading}
              >
                {t('pages.studentDetail.payments.addContribution.button')}
              </L.PrimaryActionButton>
            </L.SubblockRight>
          ) : null}
        </L.SubblockHeader>

        {contributions.length === 0 ? (
          <L.Muted>
            {t('pages.studentDetail.payments.schedule.emptyNoSchedule')}
          </L.Muted>
        ) : (
          <L.SmallTable>
            <thead>
              <tr>
                <th>{t('pages.studentDetail.payments.schedule.colDate')}</th>
                <th style={{ textAlign: 'right' }}>
                  {t('pages.studentDetail.payments.schedule.colPlan')}
                </th>
                <th>{t('pages.studentDetail.payments.schedule.colStatus')}</th>
                <th>{t('pages.studentDetail.payments.schedule.colPayment')}</th>
                <th>{t('pages.studentDetail.payments.schedule.colInvoice')}</th>
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {contributions.map((c) => {
                const status = statusOf(c);
                const { payment } = c;
                const invoice = payment?.fakturowniaInvoice;
                const remainder = c.remainder ?? 0;
                return (
                  <tr key={c.id}>
                    <td>{moment(c.date).format('DD.MM.YYYY')}</td>
                    <td style={{ textAlign: 'right' }}>
                      <MoneyCell
                        amount={c.amountForeign}
                        currency={agreement.currency || ''}
                      />
                    </td>
                    <td>
                      <L.Tag tone={STATUS_TONE[status]}>
                        {t(
                          `pages.studentDetail.payments.schedule.status.${status}`,
                        )}
                      </L.Tag>
                      {status === 'overdue' && c.delay ? (
                        <L.Muted style={{ marginLeft: 6 }}>
                          {t(
                            'pages.studentDetail.payments.schedule.delayDays',
                            {
                              count: c.delay,
                            },
                          )}
                        </L.Muted>
                      ) : null}
                      {status === 'partial' && remainder > 0 ? (
                        <L.Muted style={{ marginLeft: 6 }}>
                          {t(
                            'pages.studentDetail.payments.schedule.remainderHint',
                            {
                              amount: remainder,
                              currency: agreement.currency || '',
                            },
                          )}
                        </L.Muted>
                      ) : null}
                    </td>
                    <td>
                      {payment ? (
                        <span>
                          {moment(payment.date).format('DD.MM.YYYY')}{' '}
                          <L.Muted>
                            ·{' '}
                            {`${payment.amountAgreement} ${
                              payment.currency || ''
                            }`.trim()}
                          </L.Muted>
                        </span>
                      ) : (
                        <L.Muted>—</L.Muted>
                      )}
                    </td>
                    <td>
                      {invoice ? (
                        <L.PaymentLink
                          href={PATHS.financesInvoice.replace(
                            ':id',
                            invoice.id,
                          )}
                          onClick={(e) => {
                            e.preventDefault();
                            openInvoiceDetail(invoice.id);
                          }}
                        >
                          {invoice.number || `#${invoice.pk}`}
                        </L.PaymentLink>
                      ) : (
                        <L.Muted>—</L.Muted>
                      )}
                    </td>
                    <td
                      style={{
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        gap: 4,
                        justifyContent: 'flex-end',
                      }}
                    >
                      {c.complete ? (
                        <L.PrimaryActionButton
                          type="button"
                          onClick={() => onDeletePaidContribution(c)}
                          disabled={mutLoading || linkLoading}
                        >
                          {t(
                            'pages.studentDetail.payments.schedule.deletePayment',
                          )}
                        </L.PrimaryActionButton>
                      ) : (
                        <>
                          <L.PrimaryActionButton
                            type="button"
                            tone="primary"
                            onClick={() => setPayingContribution(c)}
                            disabled={mutLoading}
                          >
                            {t(
                              status === 'partial'
                                ? 'pages.studentDetail.payments.schedule.payRemainderAction'
                                : 'pages.studentDetail.payments.schedule.payAction',
                            )}
                          </L.PrimaryActionButton>
                          <L.PrimaryActionButton
                            type="button"
                            onClick={() => setLinkingContribution(c)}
                            disabled={mutLoading}
                          >
                            {t(
                              'pages.studentDetail.payments.schedule.linkInvoice',
                            )}
                          </L.PrimaryActionButton>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </L.SmallTable>
        )}

        {/* ─────────── MIGRATION ─────────── */}
        {orphanPayments.length > 0 ? (
          <>
            <L.SubblockHeader style={{ marginTop: 20 }}>
              {t('pages.studentDetail.payments.migration.title')}
              <L.SubblockHeaderCount>
                · {orphanPayments.length}
              </L.SubblockHeaderCount>
            </L.SubblockHeader>
            <L.Muted style={{ marginBottom: 8 }}>
              {t('pages.studentDetail.payments.migration.hint')}
            </L.Muted>
            <L.SmallTable>
              <thead>
                <tr>
                  <th>{t('pages.studentDetail.payments.date')}</th>
                  <th style={{ textAlign: 'right' }}>
                    {t('pages.studentDetail.payments.amount')}
                  </th>
                  <th>{t('pages.studentDetail.payments.linkedInvoice')}</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {orphanPayments.map((p) => {
                  const inv = p.fakturowniaInvoice;
                  return (
                    <L.OrphanRowTone key={p.id}>
                      <td>{moment(p.date).format('DD.MM.YYYY')}</td>
                      <td style={{ textAlign: 'right' }}>
                        <MoneyCell
                          amount={p.amountAgreement}
                          currency={p.currency || agreement.currency || ''}
                        />
                      </td>
                      <td>
                        {inv ? (
                          <L.PaymentLink
                            href={PATHS.financesInvoice.replace(':id', inv.id)}
                            onClick={(e) => {
                              e.preventDefault();
                              openInvoiceDetail(inv.id);
                            }}
                          >
                            {inv.number || `#${inv.pk}`}
                          </L.PaymentLink>
                        ) : (
                          <L.Muted>—</L.Muted>
                        )}
                      </td>
                      <td
                        style={{
                          textAlign: 'right',
                          whiteSpace: 'nowrap',
                          display: 'flex',
                          gap: 4,
                          justifyContent: 'flex-end',
                        }}
                      >
                        <L.PrimaryActionButton
                          type="button"
                          tone="primary"
                          onClick={() => setMigratingPayment(p)}
                          disabled={mutLoading}
                        >
                          {t(
                            'pages.studentDetail.payments.migration.linkAction',
                          )}
                        </L.PrimaryActionButton>
                        {!inv ? (
                          <L.PrimaryActionButton
                            type="button"
                            onClick={() => onDeleteOrphanPayment(p)}
                            disabled={mutLoading}
                          >
                            {t(
                              'pages.studentDetail.payments.migration.deleteAction',
                            )}
                          </L.PrimaryActionButton>
                        ) : null}
                      </td>
                    </L.OrphanRowTone>
                  );
                })}
              </tbody>
            </L.SmallTable>
          </>
        ) : null}

        {/* ─────────── POPUPS ─────────── */}
        {addingContribution ? (
          <AddContributionInlinePopup
            student={student}
            agreement={agreement}
            studentId={studentId}
            onClose={() => setAddingContribution(false)}
            onSaved={onChanged}
          />
        ) : null}
        {payingContribution ? (
          <AddPaymentInlinePopup
            agreements={[agreement]}
            defaultAgreementId={agreement.id}
            contribution={payingContribution}
            studentId={studentId}
            onClose={() => setPayingContribution(null)}
            onSaved={onChanged}
          />
        ) : null}
        {linkingContribution ? (
          <PickInvoiceForContributionPopup
            contribution={linkingContribution}
            agreement={agreement}
            studentId={studentId}
            studentEmail={studentEmail}
            onClose={() => setLinkingContribution(null)}
            onLinked={onChanged}
          />
        ) : null}
        {migratingPayment ? (
          <PickContributionForPaymentPopup
            payment={migratingPayment}
            agreement={agreement}
            studentId={studentId}
            onClose={() => setMigratingPayment(null)}
            onLinked={onChanged}
          />
        ) : null}
      </L.Subblock>
    );
  },
);
